import { Fragment } from 'react';

import { Dialog, Transition } from '@headlessui/react';

import { getSpendingLimitStatus } from '../../Dashboard.helpers';
import { Application } from '../../Dashboard.types';
import { DeclinedLimitDrawer } from './components/DeclinedLimitDrawer';
import { ExpiredLimitDrawer } from './components/ExpiredLimitDrawer';
import { ValidLimitDrawer } from './components/ValidLimitDrawer';
import { useGetSpendingLimits } from '../../hooks/useGetSpendingLimits';

export const SpendingLimitDrawer = ({
  isOpen,
  setIsOpen,
  refreshSpendingLimit,
  loadingRefreshSpendingLimit,
  errorMessage,
  application,
}: {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  refreshSpendingLimit: () => Promise<void>;
  loadingRefreshSpendingLimit: boolean;
  errorMessage: string | null;
  application: Application | null;
}) => {
  const { spendingLimits, loadingSpendingLimits } = useGetSpendingLimits();

  if (!spendingLimits || loadingSpendingLimits) return null;

  const status = getSpendingLimitStatus({
    spendingLimits: { isValid: spendingLimits.isValid },
    application,
  });

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog
        static
        open={isOpen}
        onClose={() => setIsOpen(false)}
        className="relative z-[1001]"
      >
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60" aria-hidden="true" />
        </Transition.Child>

        {/* Drawer panel */}
        <Transition.Child
          as={Fragment}
          enter="transition-transform ease-out duration-200"
          enterFrom="translate-x-full"
          enterTo="translate-x-0"
          leave="transition-transform ease-in duration-100"
          leaveFrom="translate-x-0"
          leaveTo="translate-x-full"
        >
          <Dialog.Panel
            data-ocularId="spendingLimitDrawer"
            className="fixed right-0 top-0 z-[1021] h-screen w-[300px] overflow-y-auto bg-white p-xl text-[color:var(--color-primary-10,#1e1e38)] max-sm:w-screen"
          >
            <div className="mb-l flex items-center justify-between">
              <Dialog.Title className="text-xl font-bold">
                {`Your spending limits${status === 'declined' ? '' : '*'}`}
              </Dialog.Title>
              <button
                className="cursor-pointer border-none bg-transparent p-0"
                onClick={() => setIsOpen(false)}
                aria-label="Close"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            {status === 'valid' && (
              <ValidLimitDrawer spendingLimits={spendingLimits} />
            )}
            {status === 'expired' && (
              <ExpiredLimitDrawer
                spendingLimits={spendingLimits}
                refreshSpendingLimit={refreshSpendingLimit}
                loadingRefreshSpendingLimit={loadingRefreshSpendingLimit}
                errorMessage={errorMessage}
              />
            )}
            {status === 'declined' && (
              <DeclinedLimitDrawer
                spendingLimits={spendingLimits}
                refreshSpendingLimit={refreshSpendingLimit}
                loadingRefreshSpendingLimit={loadingRefreshSpendingLimit}
                errorMessage={errorMessage}
                application={application!}
              />
            )}
          </Dialog.Panel>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
};
